// ============================================================================
// CUSTOMER GROUP MASTER — the BUSINESS HOUSE classification (ADR-0035).
// A group links multiple DISTINCT legal customers (sister concerns,
// subsidiaries, branches) under one commercial relationship so a central
// finance team can be shown a consolidated outstanding position.
// NOT entity consolidation, NOT the COA account group, NOT a segment/category.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCustomerGroups } from 'hooks/api/useCommercial';
import { customerGroupService } from 'api/services';
import type { PartyGroupDto, PartyGroupQueryParams, CreatePartyGroupRequest } from 'api/types';

type ViewMode = 'list' | 'grid';

interface FormData {
  groupCode: string;
  groupName: string;
  description: string;
  statusCode: string;
}

const EMPTY: FormData = { groupCode: '', groupName: '', description: '', statusCode: 'ACTIVE' };

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// ---------------------------------------------------------------------------
// FORM MODAL
// ---------------------------------------------------------------------------

interface GroupFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: PartyGroupDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const GroupFormModal: React.FC<GroupFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const r = recordRef.current;
    const m = modeRef.current;
    const data: FormData = r && m !== 'create'
      ? {
          groupCode: m === 'copy' ? '' : r.groupCode,
          groupName: m === 'copy' ? `${r.groupName} (Copy)` : r.groupName,
          description: r.description || '',
          statusCode: r.statusCode || 'ACTIVE',
        }
      : { ...EMPTY };
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentInstanceId = createInstanceIdRef.current || '1';
    const currentCode = recordRef.current?.groupCode;
    const savedForm = { ...formRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentInstanceId}`
      : `${tabId}-form-modal-${currentMode}-${currentCode}`;

    const title = currentMode === 'create'
      ? (savedForm.groupCode ? `Add - ${savedForm.groupCode} (${currentInstanceId})` : `Add New (${currentInstanceId})`)
      : currentMode === 'copy'
        ? `Copy - ${currentCode}`
        : `Edit - ${currentCode}`;

    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.groupCode.trim()) errors.groupCode = 'Group code is required';
    if (!form.groupName.trim()) errors.groupName = 'Group name is required';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreatePartyGroupRequest = {
        groupCode: form.groupCode.trim(),
        groupName: form.groupName.trim(),
        description: form.description.trim() || null,
        statusCode: form.statusCode,
      };
      if (mode === 'edit' && record) {
        await customerGroupService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await customerGroupService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(`Customer group "${form.groupName}" saved.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the customer group.');
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add' : mode === 'copy' ? 'Copy' : 'Edit'} Customer Group
          </h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="groupCode">
              <label className="form-label">Group Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('groupCode') ? ' form-field-error' : ''}`}
                value={form.groupCode}
                maxLength={30}
                placeholder="Enter group code"
                onChange={e => { clearFieldError('groupCode'); setForm(p => ({ ...p, groupCode: e.target.value })); }}
              />
              {getFieldError('groupCode') && <div className="form-error-message">{getFieldError('groupCode')}</div>}
            </div>

            <div className="form-group" data-field="groupName">
              <label className="form-label">Group Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('groupName') ? ' form-field-error' : ''}`}
                value={form.groupName}
                maxLength={150}
                placeholder="Enter group name"
                onChange={e => { clearFieldError('groupName'); setForm(p => ({ ...p, groupName: e.target.value })); }}
              />
              {getFieldError('groupName') && <div className="form-error-message">{getFieldError('groupName')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              options={STATUS_OPTIONS}
              value={form.statusCode}
              onChange={val => setForm(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
              isClearable={false}
            />
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={form.description}
              placeholder="Sister concerns / subsidiaries covered by this business house, central collections contact, etc."
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} this customer group?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'groupCode', label: 'Code' },
  { id: 'groupName', label: 'Group Name' },
  { id: 'partyCount', label: 'Customers' },
  { id: 'description', label: 'Description' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface CustomerGroupScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const CustomerGroupScreen: React.FC<CustomerGroupScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('customer-group');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [queryParams, setQueryParams] = useState<PartyGroupQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCustomerGroups(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('customer-group-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<PartyGroupDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: PartyGroupDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.groupCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: PartyGroupDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.groupCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.groupCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: PartyGroupDto) => {
    const ok = await confirmDelete({ resourceLabel: r.groupName });
    if (!ok) return;
    try {
      await customerGroupService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Customer group "${r.groupName}" deleted.`);
      refetch();
    } catch (err: any) {
      // The API blocks deletion while customers are still assigned — surface that reason.
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete.');
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<PartyGroupDto>[] = useMemo(() => [
    { id: 'groupCode', header: 'Code', accessorKey: 'groupCode', size: 140 },
    { id: 'groupName', header: 'Group Name', accessorKey: 'groupName', size: 260 },
    {
      id: 'partyCount', header: 'Customers', accessorKey: 'partyCount', size: 110,
      cell: ({ row }) => (
        <span style={{ textAlign: 'right', display: 'block' }}>
          {(row.original as PartyGroupDto).partyCount ?? 0}
        </span>
      ),
    },
    {
      id: 'description', header: 'Description', accessorKey: 'description', size: 300,
      cell: ({ row }) => (row.original as PartyGroupDto).description || '—',
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const r = row.original as PartyGroupDto;
        return (
          <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: PartyGroupDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Group'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const appliedCount = (codeFilter ? 1 : 0) + (nameFilter ? 1 : 0) + (includeInactive ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Customer Groups"
      description="Business houses — group sister concerns and subsidiaries for consolidated outstanding reporting"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.users size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search groups...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input type="text" className="form-input" value={codeFilter}
                  onChange={e => setCodeFilter(e.target.value)} placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Group Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={e => setNameFilter(e.target.value)} placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setCodeFilter(''); setNameFilter(''); setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({
                  ...p,
                  groupCode: codeFilter || undefined,
                  groupName: nameFilter || undefined,
                  includeInactive: includeInactive || undefined,
                  page: 1,
                }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No customer groups yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.groupCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.groupName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {r.partyCount ?? 0} customer{(r.partyCount ?? 0) === 1 ? '' : 's'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <GroupFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Customer Groups'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default CustomerGroupScreen;
